export type AvatarSize = 'sm' | 'md' | 'lg';

/** Box + text size per size, in rem so the avatar follows the reader's text size. */
export const AVATAR_SIZE_CLASSES: Record<AvatarSize, string> = {
  sm: 'h-7 w-7 text-[0.625rem]',
  md: 'h-9 w-9 text-[0.6875rem]',
  lg: 'h-12 w-12 text-sm',
};

/** Decoded-size hint for the photo request (CSS px at 1rem = 16px). */
export const AVATAR_PX: Record<AvatarSize, number> = { sm: 28, md: 36, lg: 48 };

/**
 * Deterministic colour bucket (0..5) from a name, so a person keeps their colour everywhere.
 * Same hash as Courtside's `hueOf`, so the two products agree. (PlayPulse has no
 * "(nickname)" suffix convention, so that rule is not ported.)
 */
export function avatarHue(name: string): 0 | 1 | 2 | 3 | 4 | 5 {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return (h % 6) as 0 | 1 | 2 | 3 | 4 | 5;
}

/** First letters of the first two words, uppercased. */
export function avatarInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => Array.from(w)[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Literal class strings so Tailwind sees them. Tokens live in globals.css (--avatar-N-*). */
export const AVATAR_HUE_CLASSES = [
  'bg-avatar-0 text-avatar-0-foreground',
  'bg-avatar-1 text-avatar-1-foreground',
  'bg-avatar-2 text-avatar-2-foreground',
  'bg-avatar-3 text-avatar-3-foreground',
  'bg-avatar-4 text-avatar-4-foreground',
  'bg-avatar-5 text-avatar-5-foreground',
] as const;
